import React, { useState, useEffect, useCallback } from 'react';
import './Inventory.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('name');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const PAGE_SIZE = 50;

  const fetchStats = useCallback(async () => {
    try { const res = await fetch(`${API_URL}/inventory/stats`); setStats(await res.json()); }
    catch (err) { console.error(err); }
  }, []);
  const fetchBrands = useCallback(async () => {
    try { const res = await fetch(`${API_URL}/inventory/brands`); setBrands(await res.json()); }
    catch (err) { console.error(err); }
  }, []);
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE.toString(), offset: (page * PAGE_SIZE).toString(), sort });
      if (search) params.set('search', search);
      if (brand !== 'all') params.set('brand', brand);
      if (status !== 'all') params.set('status', status);
      const res = await fetch(`${API_URL}/inventory?${params}`);
      const data = await res.json();
      setItems(data.data || []); setTotal(data.total || 0);
    } catch (err) { console.error('Failed to fetch inventory:', err); }
    finally { setLoading(false); }
  }, [page, search, brand, status, sort]);

  useEffect(() => { fetchStats(); fetchBrands(); }, [fetchStats, fetchBrands]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({ inventory_qty: item.inventory_qty || 0, reorder_level: item.reorder_level || 0, retail_price: item.retail_price || '', cost_price: item.cost_price || '', wholesale_price: item.wholesale_price || '' });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editValues.inventory_qty !== undefined) payload.inventory_qty = parseInt(editValues.inventory_qty) || 0;
      if (editValues.reorder_level !== undefined) payload.reorder_level = parseInt(editValues.reorder_level) || 0;
      if (editValues.retail_price !== undefined && editValues.retail_price !== '') payload.retail_price = parseFloat(editValues.retail_price);
      if (editValues.cost_price !== undefined && editValues.cost_price !== '') payload.cost_price = parseFloat(editValues.cost_price);
      if (editValues.wholesale_price !== undefined && editValues.wholesale_price !== '') payload.wholesale_price = parseFloat(editValues.wholesale_price);
      await fetch(`${API_URL}/inventory/${editingId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setItems(prev => prev.map(item => item.id === editingId ? { ...item, ...payload } : item));
      setEditingId(null); fetchStats();
    } catch (err) { console.error('Failed to save:', err); }
    finally { setSaving(false); }
  };

  const toggleBulkMode = () => {
    if (bulkMode) { setBulkEdits({}); setBulkMode(false); }
    else { setEditingId(null); setBulkMode(true); }
  };
  const handleBulkChange = (id, field, value) => {
    setBulkEdits(prev => {
      const existing = prev[id] || {};
      const updated = { ...existing, [field]: value };
      const item = items.find(i => i.id === id);
      const orig = { retail_price: item.retail_price || '', cost_price: item.cost_price || '', wholesale_price: item.wholesale_price || '', inventory_qty: item.inventory_qty || 0 };
      const allSame = Object.keys(updated).every(k => String(updated[k]) === String(orig[k]));
      if (allSame) { const { [id]: _, ...rest } = prev; return rest; }
      return { ...prev, [id]: updated };
    });
  };
  const handleBulkBlur = (id, field) => {
    if (field === "inventory_qty") return;
    const val = bulkEdits[id] && bulkEdits[id][field] !== undefined ? bulkEdits[id][field] : null;
    if (val !== null && val !== "") { handleBulkChange(id, field, parseFloat(val).toFixed(2)); }
  };
  const getBulkValue = (item, field) => {
    if (bulkEdits[item.id] && bulkEdits[item.id][field] !== undefined) return bulkEdits[item.id][field];
    return item[field] || (field === 'inventory_qty' ? 0 : '');
  };
  const bulkChangedCount = Object.keys(bulkEdits).length;
  const saveBulkEdits = async () => {
    if (bulkChangedCount === 0) return;
    setBulkSaving(true);
    try {
      const updates = Object.entries(bulkEdits).map(([id, changes]) => {
        const p = { id };
        if (changes.retail_price !== undefined && changes.retail_price !== '') p.retail_price = parseFloat(changes.retail_price);
        if (changes.cost_price !== undefined && changes.cost_price !== '') p.cost_price = parseFloat(changes.cost_price);
        if (changes.wholesale_price !== undefined && changes.wholesale_price !== '') p.wholesale_price = parseFloat(changes.wholesale_price);
        if (changes.inventory_qty !== undefined) p.inventory_qty = parseInt(changes.inventory_qty) || 0;
        return p;
      });
      const res = await fetch(`${API_URL}/inventory/bulk/update`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      const result = await res.json();
      if (!res.ok) { alert("Bulk save failed: " + (result.detail || result.error || "Unknown error")); setBulkSaving(false); return; }
      if (result.success) {
        setItems(prev => prev.map(item => {
          const edits = bulkEdits[item.id]; if (!edits) return item;
          const m = { ...item };
          if (edits.retail_price !== undefined && edits.retail_price !== '') m.retail_price = parseFloat(edits.retail_price);
          if (edits.cost_price !== undefined && edits.cost_price !== '') m.cost_price = parseFloat(edits.cost_price);
          if (edits.wholesale_price !== undefined && edits.wholesale_price !== '') m.wholesale_price = parseFloat(edits.wholesale_price);
          if (edits.inventory_qty !== undefined) m.inventory_qty = parseInt(edits.inventory_qty) || 0;
          return m;
        }));
        setBulkEdits({}); fetchStats();
      }
    } catch (err) { console.error('Bulk save failed:', err); }
    finally { setBulkSaving(false); }
  };

  const getRowClass = (item) => {
    if (item.inventory_qty === 0) return 'row-out-of-stock';
    if (item.reorder_level > 0 && item.inventory_qty <= item.reorder_level) return 'row-low-stock';
    return '';
  };
  const getBadgeClass = (item) => {
    if (item.inventory_qty === 0) return 'badge-out-of-stock';
    if (item.reorder_level > 0 && item.inventory_qty <= item.reorder_level) return 'badge-low-stock';
    return 'badge-in-stock';
  };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="inventory-management">
      <div className="inv-stats">
        <div className="inv-stat-card"><div className="inv-stat-number">{(stats.total_variants || 0).toLocaleString()}</div><div className="inv-stat-label">Total SKUs</div></div>
        <div className="inv-stat-card stat-green"><div className="inv-stat-number">{(stats.in_stock || 0).toLocaleString()}</div><div className="inv-stat-label">In Stock</div></div>
        <div className="inv-stat-card stat-yellow"><div className="inv-stat-number">{stats.low_stock || 0}</div><div className="inv-stat-label">Low Stock</div></div>
        <div className="inv-stat-card stat-red"><div className="inv-stat-number">{stats.out_of_stock || 0}</div><div className="inv-stat-label">Out of Stock</div></div>
        <div className="inv-stat-card"><div className="inv-stat-number">{(stats.total_units || 0).toLocaleString()}</div><div className="inv-stat-label">Total Units</div></div>
      </div>
      <div className="inv-filters">
        <input type="text" placeholder="Search by SKU or product name..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="inv-search" />
        <select value={brand} onChange={(e) => { setBrand(e.target.value); setPage(0); }} className="inv-select">
          <option value="all">All Brands</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} className="inv-select">
          <option value="all">All Status</option><option value="in">In Stock</option><option value="low">Low Stock</option><option value="out">Out of Stock</option>
        </select>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }} className="inv-select">
          <option value="name">Sort by Name</option><option value="stock_asc">Stock: Low to High</option><option value="stock_desc">Stock: High to Low</option><option value="sku">Sort by SKU</option><option value="updated">Recently Updated</option>
        </select>
        <button className={`inv-btn-bulk-toggle ${bulkMode ? 'active' : ''}`} onClick={toggleBulkMode}>{bulkMode ? 'Exit Bulk Edit' : 'Bulk Edit'}</button>
      </div>
      <div className="inv-info-bar">
        Showing {items.length} of {total.toLocaleString()} variants
        {bulkMode && bulkChangedCount > 0 && <span className="inv-bulk-count"> — {bulkChangedCount} row{bulkChangedCount > 1 ? 's' : ''} changed</span>}
      </div>
      {bulkMode && (
        <div className="inv-bulk-bar">
          <span className="inv-bulk-hint">Click any cell to edit. Changes are highlighted.</span>
          <div className="inv-bulk-actions">
            <button className="inv-btn-bulk-cancel" onClick={() => setBulkEdits({})}>Clear Changes</button>
            <button className="inv-btn-bulk-save" onClick={saveBulkEdits} disabled={bulkSaving || bulkChangedCount === 0}>{bulkSaving ? 'Saving...' : `Save All (${bulkChangedCount})`}</button>
          </div>
        </div>
      )}
      <div className="inv-grid-table">
        <div className="inv-grid-row inv-grid-header">
          <div className="inv-grid-cell">SKU</div><div className="inv-grid-cell">Product</div><div className="inv-grid-cell">Brand</div>
          <div className="inv-grid-cell">Retail</div><div className="inv-grid-cell">Cost</div><div className="inv-grid-cell">Wholesale</div>
          <div className="inv-grid-cell">Stock</div><div className="inv-grid-cell">Actions</div>
        </div>
        <div className="inv-grid-body">
          {loading ? <div className="inv-loading">Loading...</div> : items.length === 0 ? <div className="inv-empty">No variants found</div> : items.map(item => (
            <div key={item.id} className={`inv-grid-row ${getRowClass(item)} ${bulkEdits[item.id] ? 'row-bulk-changed' : ''}`}>
              <div className="inv-grid-cell inv-cell-sku">{item.sku}</div>
              <div className="inv-grid-cell inv-cell-product">{item.group_title}{(item.attribute || item.size) && <span className="inv-variant-tag">{item.attribute || item.size}</span>}</div>
              <div className="inv-grid-cell">{item.brand || '—'}</div>
              {bulkMode ? (
                <>
                  <div className="inv-grid-cell"><input type="number" className={`inv-inline-input ${bulkEdits[item.id]?.retail_price !== undefined ? 'input-changed' : ''}`} value={getBulkValue(item, 'retail_price')} onChange={(e) => handleBulkChange(item.id, 'retail_price', e.target.value)} onBlur={() => handleBulkBlur(item.id, 'retail_price')} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className={`inv-inline-input ${bulkEdits[item.id]?.cost_price !== undefined ? 'input-changed' : ''}`} value={getBulkValue(item, 'cost_price')} onChange={(e) => handleBulkChange(item.id, 'cost_price', e.target.value)} onBlur={() => handleBulkBlur(item.id, 'cost_price')} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className={`inv-inline-input ${bulkEdits[item.id]?.wholesale_price !== undefined ? 'input-changed' : ''}`} value={getBulkValue(item, 'wholesale_price')} onChange={(e) => handleBulkChange(item.id, 'wholesale_price', e.target.value)} onBlur={() => handleBulkBlur(item.id, 'wholesale_price')} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className={`inv-inline-input ${bulkEdits[item.id]?.inventory_qty !== undefined ? 'input-changed' : ''}`} value={getBulkValue(item, 'inventory_qty')} onChange={(e) => handleBulkChange(item.id, 'inventory_qty', e.target.value)} /></div>
                  <div className="inv-grid-cell inv-cell-actions">{bulkEdits[item.id] && <span className="inv-changed-dot">●</span>}</div>
                </>
              ) : editingId === item.id ? (
                <>
                  <div className="inv-grid-cell"><input type="number" className="inv-inline-input" value={editValues.retail_price} onChange={(e) => setEditValues(v => ({ ...v, retail_price: e.target.value }))} onBlur={() => setEditValues(v => ({ ...v, retail_price: v.retail_price !== '' ? parseFloat(v.retail_price).toFixed(2) : '' }))} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className="inv-inline-input" value={editValues.cost_price} onChange={(e) => setEditValues(v => ({ ...v, cost_price: e.target.value }))} onBlur={() => setEditValues(v => ({ ...v, cost_price: v.cost_price !== '' ? parseFloat(v.cost_price).toFixed(2) : '' }))} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className="inv-inline-input" value={editValues.wholesale_price} onChange={(e) => setEditValues(v => ({ ...v, wholesale_price: e.target.value }))} onBlur={() => setEditValues(v => ({ ...v, wholesale_price: v.wholesale_price !== '' ? parseFloat(v.wholesale_price).toFixed(2) : '' }))} step="0.01" /></div>
                  <div className="inv-grid-cell"><input type="number" className="inv-inline-input" value={editValues.inventory_qty} onChange={(e) => setEditValues(v => ({ ...v, inventory_qty: parseInt(e.target.value) || 0 }))} /></div>
                  <div className="inv-grid-cell inv-cell-actions">
                    <button className="inv-btn-save" onClick={saveEdit} disabled={saving}>{saving ? '...' : 'Save'}</button>
                    <button className="inv-btn-cancel" onClick={cancelEdit}>✕</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="inv-grid-cell">${parseFloat(item.retail_price || 0).toFixed(2)}</div>
                  <div className="inv-grid-cell">{item.cost_price ? `${parseFloat(item.cost_price).toFixed(2)}` : '—'}</div>
                  <div className="inv-grid-cell">{item.wholesale_price ? `${parseFloat(item.wholesale_price).toFixed(2)}` : '—'}</div>
                  <div className="inv-grid-cell"><span className={`inv-stock-badge ${getBadgeClass(item)}`}>{item.inventory_qty}</span></div>
                  <div className="inv-grid-cell inv-cell-actions"><button className="inv-btn-edit" onClick={() => startEdit(item)}>Edit</button></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="inv-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default Inventory;
